import { GatsbyImage } from 'gatsby-plugin-image';
import pt from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { combine } from '../../../helpers/styles';
import { swapImageUrl } from '../../../helpers/swapImageURI';
import AwardBadge from '../awardBadge';
import Icon from '../icon';
import RichText from '../richText';
import placeholderImage from '../../../images/placeholder-image.png';
import * as styles from './styles.module.scss';

const Image = ({
  image,
  imageCaption,
  disableGatsbyImage,
  disableImageCaption,
  className,
  imgClassName,
  captionClass,
  badgeIcon,
  badgeYear,
  isZoomable,
  focusPoint,
  format,
  loading,
}) => {
  const [error, setError] = useState(false);
  const imageElement1 = useRef();
  const imageElement2 = useRef();

  useEffect(() => {
    let img = null;

    if (imageElement1?.current) img = imageElement1.current;
    if (imageElement2?.current) img = imageElement2.current;

    if (img) {
      setError(img.complete && !img.naturalHeight && !img.naturalWidth);
    }
  }, []);

  if ((image && image.url) || disableGatsbyImage) {
    const stagingPrefix = 'https://ifservice';
    const productionPrefix = 'https://ifinfralivestorage';

    if (
      image.url.startsWith(productionPrefix) ||
      image.url.startsWith(stagingPrefix)
    ) {
      image.url = swapImageUrl(image.url);
    }
    return (
      <div
        className={combine(
          className,
          format && format !== 'original' && styles.formatWrapper,
          format && format === '4:3' && styles.format43,
          format && format === '3:4' && styles.format34,
          format && format === '1:1' && styles.format11,
          format && format === '16:9' && styles.format169,
          focusPoint && focusPoint === 'left' && styles.leftAligned,
          focusPoint && focusPoint === 'center' && styles.centerAligned,
          focusPoint && focusPoint === 'right' && styles.rightAligned
        )}
      >
        <>
          {isZoomable && (
            <Icon
              name="times"
              type="fal"
              className={styles.closeBtn}
              size="2x"
              color="white"
            />
          )}
          <img
            loading={loading}
            src={!error ? image.url : placeholderImage}
            alt={image.description}
            className={combine(
              imgClassName,
              format && format !== 'original' && styles.img
            )}
            draggable="false"
            onError={({ currentTarget }) => {
              currentTarget.onerror = null;
              currentTarget.src = placeholderImage;
            }}
            ref={imageElement1}
          />
          {badgeIcon && (
            <AwardBadge
              name={badgeIcon}
              className={combine(styles.badge, styles.badgeIcon)}
            />
          )}
        </>
      </div>
    );
  }

  if (image && image.file && image?.file?.contentType?.indexOf('svg') >= 0) {
    return (
      <div className={className}>
        <img
          src={!error ? `https:${image.file.url}` : placeholderImage}
          alt={image.description}
          className={combine(styles.svgImg, imgClassName)}
          draggable="false"
          onError={({ currentTarget }) => {
            currentTarget.onerror = null;
            currentTarget.src = placeholderImage;
          }}
          ref={imageElement2}
        />
        {badgeIcon && (
          <AwardBadge
            name={badgeIcon}
            year={badgeYear}
            className={combine(styles.badge, styles.badgeIcon)}
          />
        )}
      </div>
    );
  }

  return (
    <>
      <GatsbyImage
        image={image?.gatsbyImageData}
        alt={image?.description}
        className={combine(
          className,
          format && format !== 'original' && styles.formatWrapper,
          format && format === '4:3' && styles.format43,
          format && format === '3:4' && styles.format34,
          format && format === '1:1' && styles.format11,
          format && format === '16:9' && styles.format169,
          focusPoint && focusPoint === 'left' && styles.leftAligned,
          focusPoint && focusPoint === 'center' && styles.centerAligned,
          focusPoint && focusPoint === 'right' && styles.rightAligned
        )}
        imgClassName={combine(
          imgClassName,
          format && format !== 'original' && styles.img
        )}
        draggable="false"
      />
      {imageCaption && !disableImageCaption && (
        <RichText
          content={imageCaption}
          className={combine(styles.caption, captionClass)}
        />
      )}
      {badgeIcon && (
        <AwardBadge
          name={badgeIcon}
          className={combine(styles.badge, styles.badgeIcon)}
        />
      )}
    </>
  );
};

export const imageProptypes = {
  url: pt.string,
  file: pt.shape({
    url: pt.string,
  }),
  description: pt.string,
  gatsbyImageData: pt.object,
};

Image.propTypes = {
  image: pt.shape(imageProptypes).isRequired,
  imageCaption: pt.object,
  disableGatsbyImage: pt.bool,
  disableImageCaption: pt.bool,
  className: pt.string,
  captionClass: pt.string,
  imgClassName: pt.string,
  badgeIcon: pt.string,
  badgeYear: pt.number,
  isZoomable: pt.bool,
  focusPoint: pt.oneOf(['left', 'center', 'right']),
  format: pt.oneOf(['original', '4:3', '3:4', '16:9']),
  closeFn: pt.func,
  loading: pt.string,
};

Image.defaultProps = {
  className: '',
  imageCaption: null,
  disableGatsbyImage: false,
  disableImageCaption: false,
  captionClass: '',
  imgClassName: '',
  badgeYear: 2021,
  badgeIcon: '',
  isZoomable: false,
  focusPoint: 'center',
  format: 'original',
  closeFn: () => {},
  loading: 'eager',
};

export default Image;
